// Precarga de los chunks lazy de las secciones del sidebar.
// ─────────────────────────────────────────────────────────────────────────
// Las páginas del campus se cargan con React.lazy (code-splitting). La primera
// vez que se pulsa un item del menú, su chunk aún no está en memoria y React
// suspende un instante. Con el Suspense interno + startTransition ya no se ve
// el spinner, pero precargar los chunks en segundo plano (cuando el navegador
// está ocioso) hace que la conmutación sea INSTANTÁNEA desde el primer clic.
//
// Importante: son los MISMOS `import()` que usa App.tsx con React.lazy. El
// bundler deduplica el módulo, así que llamar aquí solo calienta la caché de
// módulos del navegador; no descarga nada dos veces.

/** Imports de las secciones alcanzables desde el sidebar (orden de prioridad). */
const ROUTE_IMPORTS: Array<() => Promise<unknown>> = [
  () => import('../pages/Curso'),
  () => import('../pages/Progreso'),
  () => import('../pages/MyRobots'),
  () => import('../pages/MyPlan'),
  () => import('../pages/TiendaEAs'),
  () => import('../pages/Mercado'),
  () => import('../pages/Comunidad'),
  () => import('../pages/ModuleDetail'),
  // Admin (solo se precarga si el usuario es admin · ver preloadAdminRoutes).
];

/** Imports de las secciones del Panel admin (submenú). */
const ADMIN_ROUTE_IMPORTS: Array<() => Promise<unknown>> = [
  () => import('../pages/Admin'),
  () => import('../pages/AdminUsuarios'),
  () => import('../pages/AdminAnalitica'),
  () => import('../pages/AdminContenido'),
  () => import('../pages/AdminLicencias'),
  () => import('../pages/AdminActividad'),
  () => import('../pages/AdminIntegraciones'),
];

let started = false;

/** Lanza las precargas de forma escalonada y ociosa (no bloquea el hilo). */
function runWhenIdle(imports: Array<() => Promise<unknown>>): void {
  const schedule =
    typeof window !== 'undefined' && 'requestIdleCallback' in window
      ? (cb: () => void) => (window as Window & {
          requestIdleCallback: (cb: () => void) => number;
        }).requestIdleCallback(cb)
      : (cb: () => void) => window.setTimeout(cb, 200);

  imports.forEach((imp, i) => {
    schedule(() => {
      // Escalona un poco para no competir con el render inicial.
      window.setTimeout(() => {
        // Silenciamos errores: una precarga fallida no debe romper nada; el
        // import real volverá a intentarse al navegar.
        void imp().catch(() => {});
      }, i * 60);
    });
  });
}

/**
 * Precarga las secciones comunes del sidebar. Idempotente: solo corre una vez
 * por sesión de página. Llamar al montar el shell.
 */
export function preloadSidebarRoutes(isAdmin: boolean): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  runWhenIdle(ROUTE_IMPORTS);
  if (isAdmin) runWhenIdle(ADMIN_ROUTE_IMPORTS);
}
